import { ArrowLeft, Shield, Video } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-sarathi-background text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-sarathi-surface/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/"
            className="w-9 h-9 rounded-full bg-sarathi-background border border-white/10 flex items-center justify-center text-sarathi-text-muted hover:text-white transition-colors"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-sarathi-primary" />
            <h1 className="font-outfit text-lg font-semibold">Privacy Policy</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8" data-testid="privacy-policy-content">
        <div>
          <p className="text-sarathi-text-muted text-sm">Last updated: May 2026</p>
          <h2 className="font-outfit text-2xl font-bold mt-2">Sarathi-X Privacy Policy</h2>
          <p className="text-sarathi-text-secondary mt-3 leading-relaxed">
            Sarathi-X ("we", "us", "our") is committed to protecting your personal data. This policy explains what data
            we collect, why we collect it, how long we keep it, and your rights under the Digital Personal Data Protection
            Act, 2023 (DPDP Act).
          </p>
        </div>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">1. Data We Collect</h3>
          <ul className="list-disc list-inside text-sarathi-text-secondary space-y-1.5 leading-relaxed pl-2">
            <li><strong className="text-white">Name</strong> — To identify the passenger for pickup</li>
            <li><strong className="text-white">Phone Number</strong> — Primary contact for ride coordination and WhatsApp updates</li>
            <li><strong className="text-white">Email</strong> (optional) — For sending booking receipts</li>
            <li><strong className="text-white">Pickup & Drop Locations</strong> — To calculate fare and plan the route</li>
            <li><strong className="text-white">Ride Date & Time</strong> — To schedule the vehicle</li>
            <li><strong className="text-white">Flight Number</strong> (optional) — To track delays for airport pickups</li>
            <li><strong className="text-white">Special Requests</strong> (optional) — To fulfil accessibility or luggage needs</li>
            <li><strong className="text-white">Dashcam footage</strong> (road-facing) — Recorded during the trip for safety and incident documentation</li>
            <li><strong className="text-white">Cabin camera footage</strong> — Recorded during the trip for passenger and driver safety</li>
            <li><strong className="text-white">GPS and telematics data</strong> — Vehicle location, speed, and driving behaviour captured by the in-vehicle device</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">2. Purpose of Collection</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">
            All personal data is collected solely for the purpose of <strong className="text-white">booking fulfilment</strong> — scheduling
            your ride, calculating fares, coordinating with the driver, and sending you booking confirmations. We do not
            use your data for marketing, profiling, or advertising.
          </p>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Video and telematics data collected via Cautio hardware is used exclusively for:
          </p>
          <ul className="list-disc list-inside text-sarathi-text-secondary space-y-1.5 leading-relaxed pl-2">
            <li>Passenger and driver safety monitoring</li>
            <li>Incident documentation and dispute resolution</li>
            <li>Driver performance and safety improvement</li>
          </ul>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Footage is not used for advertising, sold to third parties, or shared beyond what is described in Section 3.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">3. Data Sharing</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Your personal data is <strong className="text-white">never sold to third parties</strong>. Data is shared only with:
          </p>
          <ul className="list-disc list-inside text-sarathi-text-secondary space-y-1.5 leading-relaxed pl-2">
            <li>Our assigned driver — name, phone, pickup/drop for ride execution</li>
            <li>Google Maps Platform — addresses only, for route and fare calculation (governed by Google's privacy policy)</li>
            <li>
              <strong className="text-white">Cautio</strong> (Covert Eye Technologies Private Limited) — video footage and telematics data are
              processed and stored on Cautio's cloud platform, hosted on secure, industry-standard infrastructure.
              Cautio acts as a data processor on behalf of Sarathi-X and does not use your data for its own commercial
              purposes beyond anonymised, aggregated analytics. Cautio's privacy policy is available at{" "}
              <a href="https://cautio.com" target="_blank" rel="noopener noreferrer" className="text-sarathi-primary hover:underline">
                cautio.com
              </a>
              .
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">4. Data Retention</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Booking records are retained for <strong className="text-white">12 months</strong> from the ride date for accounting
            and dispute resolution. After 12 months, personally identifiable data (name, phone, email) is anonymised.
            Aggregated fare and route statistics may be retained indefinitely.
          </p>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Video footage captured by Cautio hardware is retained for the duration specified in Sarathi-X's agreement
            with Cautio. Footage may be retained longer if required for an active dispute, insurance claim, or legal
            proceeding, after which it is deleted.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">5. Your Rights (DPDP Act 2023)</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">Under the DPDP Act, you have the right to:</p>
          <ul className="list-disc list-inside text-sarathi-text-secondary space-y-1.5 leading-relaxed pl-2">
            <li><strong className="text-white">Access</strong> — Request a copy of the personal data we hold about you</li>
            <li><strong className="text-white">Correction</strong> — Request correction of inaccurate data</li>
            <li><strong className="text-white">Erasure</strong> — Request deletion of your data (subject to legal retention requirements)</li>
            <li><strong className="text-white">Grievance Redressal</strong> — Raise a complaint about data handling</li>
          </ul>
        </section>

        <section className="space-y-3" data-testid="in-vehicle-recording-notice">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">5A. In-Vehicle Recording Notice</h3>
          <div className="bg-sarathi-primary/5 border border-sarathi-primary/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-sarathi-primary" />
              <p className="text-white font-semibold">This vehicle is equipped with dashcams</p>
            </div>
            <p className="text-sarathi-text-secondary leading-relaxed text-sm">
              Sarathi-X vehicles are fitted with Cautio road-facing and cabin cameras. By travelling in this vehicle, you
              consent to recording for safety and incident documentation purposes. Footage is stored securely and handled
              in accordance with this Privacy Policy.
            </p>
            <p className="text-sarathi-text-secondary leading-relaxed text-sm">
              Passengers who do not consent to recording may request a refund before the ride commences by contacting
              Sarathi-X at{" "}
              <a href="mailto:founder@sarathix.in" className="text-sarathi-primary hover:underline">founder@sarathix.in</a>.
              Once a trip has begun, recording cannot be disabled for safety reasons.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">6. Data Security</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">
            We protect your data with industry-standard measures including encrypted database connections, rate-limited APIs,
            security headers (CSP, HSTS, X-Frame-Options), and admin access secured with two-factor authentication (TOTP).
          </p>
          <p className="text-sarathi-text-secondary leading-relaxed">
            Video and telematics data processed by Cautio is stored on encrypted, redundant cloud infrastructure. Access
            is restricted to authorised Sarathi-X and Cautio personnel only.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="font-outfit text-lg font-semibold text-sarathi-primary">7. Contact for Data Requests</h3>
          <p className="text-sarathi-text-secondary leading-relaxed">
            To exercise any of your data rights, raise a concern about data handling, or submit a dashcam footage access
            request, contact us at:
          </p>
          <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4 space-y-1">
            <p className="text-white font-medium">Sarathi-X Private Limited</p>
            <p className="text-sarathi-text-secondary text-sm">Eagleton The Golf Resort, Mysore Road, Bengaluru — 562109</p>
            <p className="text-sarathi-text-secondary text-sm">
              Email: <a href="mailto:founder@sarathix.in" className="text-sarathi-primary hover:underline">founder@sarathix.in</a>
            </p>
          </div>
          <p className="text-sarathi-text-muted text-sm mt-2">
            We will respond to valid requests within 30 days as required by the DPDP Act.
          </p>
        </section>

        <div className="border-t border-white/10 pt-6 text-center">
          <Link
            to="/"
            className="text-sarathi-primary hover:underline text-sm font-medium"
            data-testid="privacy-home-link"
          >
            Back to Sarathi-X Home
          </Link>
        </div>
      </div>
    </div>
  );
}
